import { createInterval, type ReadonlyDate } from '..'

/**
 * This number is small enough to keep the timer precise enough for daily usage,
 * But not to make its interval execute too many times
 */
export const TIME_THRESHOLD = 500

const calculateTimeLeft = (targetTimestamp: number): number => targetTimestamp - Date.now()

const createSimpleTimeout = (callback: VoidFunction, time: number): VoidFunction => {
    const id = setTimeout(callback, time)
    return () => clearTimeout(id)
}

/**
 * Create a cancelable timeout
 *
 * @param callback will be executed at the end of the given time or date.
 *  **May** be executed further into the future if process/idle is stopped
 *  But it is designed to be robust against them by ensuring the given callback
 *  will be executed at most {TIME_THRESHOLD}ms after it should
 * @param timeOrDate can be:
 *  - number -> in milliseconds, negative values are allowed
 *  - Date -> absolute time to wait by, past dates are allowed
 * @returns a cancel function, call it to cancel the callback execution
 * @see {TIME_THRESHOLD}
 */
export const createTimeout = (callback: VoidFunction, timeOrDate: number | ReadonlyDate): VoidFunction => {
    const timestamp = typeof timeOrDate === 'number' ? timeOrDate + Date.now() : timeOrDate.getTime()
    const timerStoppers: VoidFunction[] = []

    /**
     * Stop all timers and clear them for further execution
     */
    const stopAndClear = (): void => {
        for (const stopper of timerStoppers) {
            stopper()
        }
        timerStoppers.splice(0)
    }

    if (calculateTimeLeft(timestamp) < TIME_THRESHOLD) {
        /**
         * Time given is under threshold, no reason to do anything fancy
         */
        timerStoppers.push(createSimpleTimeout(callback, calculateTimeLeft(timestamp)))
    } else {
        /**
         * Time given is above threhsold, so setup a timer to re-check it often enough
         * Every cycle then, further check if execution has fallen under the threshold
         * If it has, kill interval, and setup the simple timer
         */
        timerStoppers.push(
            createInterval(() => {
                if (calculateTimeLeft(timestamp) > TIME_THRESHOLD) {
                    return
                }

                stopAndClear()
                timerStoppers.push(createSimpleTimeout(callback, calculateTimeLeft(timestamp)))
            }, TIME_THRESHOLD)
        )
    }

    return stopAndClear
}
